/**
 * OAuth Authentication Module
 *
 * Handles Google and Microsoft OAuth login flows.
 */

import api from '@monorepo/utils/apiRequest';

// Use the same API base URL as everything else (goes through proxy)
const API_BASE_URL = import.meta.env?.VITE_API_BASE_URL || '/api';

/**
 * Get OAuth provider status (which providers are configured)
 * @returns {Promise<{google: boolean, microsoft: boolean}>}
 */
export const getOAuthStatus = () => api.get('/auth/status');

/**
 * Initiate Google OAuth login
 * Redirects user to Google login page
 */
export const loginWithGoogle = () => {
  const origin = encodeURIComponent(window.location.origin);
  window.location.href = `${API_BASE_URL}/users/oauth/google/login?redirect_to=${origin}`;
};

/**
 * Initiate Microsoft OAuth login
 * Redirects user to Microsoft login page
 */
export const loginWithMicrosoft = () => {
  const origin = encodeURIComponent(window.location.origin);
  window.location.href = `${API_BASE_URL}/users/oauth/microsoft/login?redirect_to=${origin}`;
};

/**
 * OAuth error messages mapping
 */
export const OAUTH_ERROR_MESSAGES = {
  oauth_not_configured: 'OAuth login is not configured. Please contact support.',
  email_required: 'Could not retrieve email from OAuth provider. Please try again.',
  no_account: 'No account found with this email. Please contact your administrator.',
  oauth_already_linked: 'Your account is already linked to a different OAuth provider.',
  oauth_failed: 'OAuth login failed. Please try again.',
  device_limit_reached: 'You are already logged in on 2 devices. Please log out from one device first.',
  account_deactivated: 'Your account has been deactivated. Please contact support.'
};

/**
 * Parse OAuth error from URL query parameters
 * @param {URLSearchParams} searchParams
 * @returns {{error: string, message: string, email?: string, provider?: string, emergencyToken?: string, canEmergencyRevoke?: boolean} | null}
 */
export const parseOAuthError = (searchParams) => {
  const error = searchParams.get('error');
  if (!error) return null;

  return {
    error,
    message: OAUTH_ERROR_MESSAGES[error] || 'An error occurred during login.',
    email: searchParams.get('email'),
    provider: searchParams.get('provider'),
    emergencyToken: searchParams.get('emergency_token'),
    canEmergencyRevoke: searchParams.get('can_emergency_revoke') === 'true'
  };
};
